/**
 * ADR-173 D1 — the contract-term fields, one implementation.
 *
 * Exported one by one so a surface with its own grid (the employee form) can
 * place each field where it sits today, and stacked as <ContractTermsFields />
 * for a dialog (Add Contract). Labels, helper texts and the company-picker
 * configuration come from constants/contractTerms.js — never set them here
 * per caller.
 */
import React from 'react';
import { TextField } from '@mui/material';
import EmployeeContractTypeSelector from '../../common/EmployeeContractTypeSelector';
import EmployeeEngagementModelSelector from '../../common/EmployeeEngagementModelSelector';
import CompanySelector from '../../common/CompanySelector';
import {
  CONTRACT_TERM_LABELS,
  CONTRACT_TERM_HELPER_TEXTS,
  CONTRACT_COMPANY_SELECTOR_PROPS
} from '../../../constants/contractTerms';

export const ContractTypeField = ({ value, onChange, label = CONTRACT_TERM_LABELS.contractType, required = true, disabled = false }) => (
  <EmployeeContractTypeSelector value={value} onChange={onChange} label={label} required={required} disabled={disabled} />
);

export const ContractCompanyField = ({ value, onChange, warnClusterId = null, label = CONTRACT_TERM_LABELS.contractCompany, required = true, disabled = false }) => (
  <CompanySelector
    value={value}
    onChange={onChange}
    label={label}
    warnClusterId={warnClusterId || null}
    {...CONTRACT_COMPANY_SELECTOR_PROPS}
    required={required}
    disabled={disabled}
  />
);

export const EngagementModelField = ({ value, onChange, label = CONTRACT_TERM_LABELS.engagementModel, required = false, disabled = false }) => (
  <EmployeeEngagementModelSelector value={value} onChange={onChange} label={label} required={required} disabled={disabled} />
);

// ADR-174 — `helperText`, `error` and `autoFocus` let the Contract Number
// dialog reuse this control (one contract-number input, ADR-173). Defaults
// are unchanged for every existing caller.
export const ContractNumberField = ({
  value, onChange, disabled = false,
  helperText = CONTRACT_TERM_HELPER_TEXTS.contractNumber, error = false, autoFocus = false
}) => (
  <TextField
    label={CONTRACT_TERM_LABELS.contractNumber}
    fullWidth
    value={value}
    onChange={(event) => onChange(event.target.value)}
    disabled={disabled}
    helperText={helperText}
    error={error}
    autoFocus={autoFocus}
    inputProps={{ inputMode: 'text', spellCheck: false }}
  />
);

/**
 * The four term fields stacked, for a dialog. `values` holds
 * { contractType, contractCompany, engagementModel, contractNumber };
 * `onChange(field, value)` receives the field key and the new value.
 */
const ContractTermsFields = ({ values, onChange, warnClusterId = null, disabled = false }) => (
  <>
    <ContractTypeField value={values.contractType} onChange={(v) => onChange('contractType', v)} disabled={disabled} />
    <ContractCompanyField value={values.contractCompany} onChange={(v) => onChange('contractCompany', v)} warnClusterId={warnClusterId} disabled={disabled} />
    <EngagementModelField value={values.engagementModel} onChange={(v) => onChange('engagementModel', v)} disabled={disabled} />
    <ContractNumberField value={values.contractNumber} onChange={(v) => onChange('contractNumber', v)} disabled={disabled} />
  </>
);

export default ContractTermsFields;
